import m, {ClassComponent, Vnode, VnodeDOM} from 'mithril';
import {ALL_SELECTED, RenderContext, SegmentControlCondition, SegmentControls, SegmentSelectionValues} from '../types';

type Segment = SegmentControls['segments'][number];
type Labels = NonNullable<RenderContext['labels']>;
/** Checked values by condition id while the menu is open; every option checked is "all" */
type Draft = Record<string, string[]>;
export type Selection = Record<string, SegmentSelectionValues>;

// Mithril 2.2 redraws after event handlers of a separately rendered tree only when given a callback; its typings omit it.
const render = m.render as (el: Element, vnodes: m.Children, redraw?: () => void) => void;

const MENU_GAP = 4;
const VIEWPORT_MARGIN = 8;

export interface SegmentPickerAttrs {
    segment: Segment;
    labels: Labels;
    /** The new selection of the segment, by condition id */
    onapply: (selection: Selection) => void;
}

function checkedValues(condition: SegmentControlCondition): string[] {
    const options = condition.options.map(option => option.value);
    return condition.selected === ALL_SELECTED ? options : options.filter(value => condition.selected.includes(value));
}

function initialDraft(segment: Segment): Draft {
    return Object.fromEntries(segment.conditions.map(condition => [condition.id, checkedValues(condition)]));
}

function selectedLabels(condition: SegmentControlCondition): string[] {
    const checked = checkedValues(condition);
    return condition.options.filter(option => checked.includes(option.value)).map(option => option.label);
}

/** One condition in the combo: its option, a count, or null when it puts no constraint */
function conditionText(condition: SegmentControlCondition, labels: Labels): string | null {
    if (condition.selected === ALL_SELECTED) {
        return null;
    }
    const chosen = selectedLabels(condition);
    if (chosen.length <= 1) {
        return chosen[0] ?? labels.none_selected_text ?? 'none_selected_text';
    }
    return (labels.count_selected_text ?? 'count_selected_text').replace('{0}', String(chosen.length));
}

/** What the combo shows: the narrowing conditions, or "all" when none narrows */
export function segmentText(segment: Segment, labels: Labels): string {
    return segment.conditions.map(condition => conditionText(condition, labels)).filter(text => text !== null).join(' · ')
        || (labels.select_all_text ?? 'select_all_text');
}

/** The selection in words, as the server summarizes a segment without a label: only the narrowing conditions */
export function segmentSummary(segment: Segment, labels: Labels): string {
    return segment.conditions
        .filter(condition => condition.selected !== ALL_SELECTED)
        .map(condition => selectedLabels(condition).join(', ') || (labels.none_selected_text ?? 'none_selected_text'))
        .join(' · ')
        || (labels.select_all_text ?? 'select_all_text');
}

/** The full selection per condition, the combo's tooltip */
export function segmentTitle(segment: Segment, labels: Labels): string {
    return segment.conditions.map(condition => {
        const values = condition.selected === ALL_SELECTED
            ? labels.select_all_text ?? 'select_all_text'
            : selectedLabels(condition).join(', ') || (labels.none_selected_text ?? 'none_selected_text');
        return `${condition.label}: ${values}`;
    }).join('\n');
}

/**
 * What Apply sends, null when the draft still checks what the picker opened with. Comparing drafts, not the sent form:
 * a stored list of every option would read as "all" and refetch different data although the viewer changed nothing.
 */
export function appliedSelection(segment: Segment, draft: Draft): Selection | null {
    const changed = segment.conditions.some(condition => (draft[condition.id] ?? []).join('\n') !== checkedValues(condition).join('\n'));
    return changed ? draftSelection(segment, draft) : null;
}

/**
 * The draft as the selection to send: a condition with every option checked is "all", so it keeps meaning every option
 * within the viewer's restriction. A condition without options to check keeps what it had.
 */
export function draftSelection(segment: Segment, draft: Draft): Selection {
    return Object.fromEntries(segment.conditions.map(condition => {
        const checked = draft[condition.id] ?? [];
        if (condition.options.length === 0) {
            return [condition.id, condition.selected];
        }
        const all = condition.options.every(option => checked.includes(option.value));
        return [condition.id, all ? ALL_SELECTED : checked];
    }));
}

/**
 * The viewer's choice of one segment's values: a combo showing the selection opens a checkbox list per condition,
 * applied at once (one refetch). The menu renders into the body: the table scrolls and would clip it.
 */
export default class SegmentPicker implements ClassComponent<SegmentPickerAttrs> {
    /** Selection being edited; null while closed */
    private draft: Draft | null = null;
    private host: HTMLElement | null = null;
    private button: HTMLElement | null = null;
    /** Moves the focus into the menu once it is rendered */
    private focusMenu = false;
    private readonly reposition = () => this.position();
    private readonly outside = (e: Event) => {
        const target = e.target as Node;
        if (!this.host?.contains(target) && !this.button?.contains(target)) {
            this.close(false);
        }
    };
    private readonly escape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            this.close(true);
        }
    };

    oncreate({dom, attrs}: VnodeDOM<SegmentPickerAttrs>): void {
        this.button = dom as HTMLElement;
        this.renderMenu(attrs);
    }

    onupdate({dom, attrs}: VnodeDOM<SegmentPickerAttrs>): void {
        this.button = dom as HTMLElement;
        this.renderMenu(attrs);
    }

    onremove(): void {
        this.detach();
    }

    view({attrs}: Vnode<SegmentPickerAttrs>): m.Children {
        const text = segmentText(attrs.segment, attrs.labels);

        return m('button.form-select.form-select-sm.db-v2-segment-combo.d-print-none', {
            type: 'button',
            title: segmentTitle(attrs.segment, attrs.labels),
            'aria-label': `${attrs.labels.segmentPickerTitle ?? 'segmentPickerTitle'}: ${text}`,
            'aria-haspopup': 'true',
            'aria-expanded': String(this.draft !== null),
            'data-html2canvas-ignore': '',
            onclick: () => {
                this.draft = this.draft ? null : initialDraft(attrs.segment);
                this.focusMenu = this.draft !== null;
            },
        }, m('span', text));
    }

    /** `refocus`: closed from the keyboard or the menu's buttons, so the focus goes back to the picker button */
    private close(refocus: boolean): void {
        this.draft = null;
        if (refocus) {
            this.button?.focus();
        }
        m.redraw();
    }

    private renderMenu(attrs: SegmentPickerAttrs): void {
        if (this.draft === null) {
            this.detach();
            return;
        }
        if (!this.host) {
            this.host = document.createElement('div');
            document.body.appendChild(this.host);
            window.addEventListener('scroll', this.reposition, true);
            window.addEventListener('resize', this.reposition);
            document.addEventListener('mousedown', this.outside, true);
            document.addEventListener('keydown', this.escape);
        }
        render(this.host, this.menu(attrs, this.draft), () => m.redraw());
        this.position();
        if (this.focusMenu) {
            this.focusMenu = false;
            this.host.querySelector<HTMLElement>('input, button')?.focus();
        }
    }

    private detach(): void {
        if (!this.host) {
            return;
        }
        window.removeEventListener('scroll', this.reposition, true);
        window.removeEventListener('resize', this.reposition);
        document.removeEventListener('mousedown', this.outside, true);
        document.removeEventListener('keydown', this.escape);
        m.render(this.host, null);
        this.host.remove();
        this.host = null;
    }

    private position(): void {
        const menu = this.host?.firstElementChild as HTMLElement | null;
        if (!menu || !this.button) {
            return;
        }
        const rect = this.button.getBoundingClientRect();
        const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - menu.offsetWidth - VIEWPORT_MARGIN));
        menu.style.top = `${rect.bottom + MENU_GAP}px`;
        menu.style.left = `${left}px`;
        menu.style.maxHeight = `${Math.max(window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN, 160)}px`;
    }

    private menu(attrs: SegmentPickerAttrs, draft: Draft): m.Children {
        const {segment, labels} = attrs;
        const set = (id: string, values: string[]) => {
            this.draft = {...draft, [id]: values};
        };
        const fullyChecked = (condition: SegmentControlCondition) => condition.options.every(option => (draft[condition.id] ?? []).includes(option.value));
        const everything = segment.conditions.every(fullyChecked);

        return m('.dropdown-menu.show.db-v2-segment-menu', {role: 'dialog', 'aria-label': labels.segmentPickerTitle ?? 'segmentPickerTitle'}, [
            segment.conditions.length > 1 && m('label.dropdown-item.d-flex.align-items-center.gap-2.db-v2-segment-all', [
                m('input.form-check-input.m-0', {
                    type: 'checkbox',
                    checked: everything,
                    onchange: () => {
                        this.draft = Object.fromEntries(segment.conditions.map(condition => [
                            condition.id,
                            everything ? [] : condition.options.map(option => option.value),
                        ]));
                    },
                }),
                m('span', labels.select_all_text ?? 'select_all_text'),
            ]),
            segment.conditions.map(condition => {
                const chosen = draft[condition.id] ?? [];
                const all = condition.options.length > 0 && fullyChecked(condition);

                return m('fieldset.db-v2-segment-condition', {key: condition.id}, [
                    m('legend.dropdown-header.d-flex.align-items-center.gap-2', [
                        m('span.flex-grow-1', condition.label),
                        condition.options.length > 1 && m('button.btn.btn-link.btn-sm.p-0', {
                            type: 'button',
                            onclick: () => set(condition.id, all ? [] : condition.options.map(option => option.value)),
                        }, all ? labels.deselectAll ?? 'deselectAll' : labels.selectAll ?? 'selectAll'),
                    ]),
                    condition.options.map(option => m('label.dropdown-item.d-flex.align-items-center.gap-2', {key: option.value}, [
                        m('input.form-check-input.m-0', {
                            type: 'checkbox',
                            checked: chosen.includes(option.value),
                            // Kept in the options' order, which is the order the server summarizes in
                            onchange: (e: Event) => set(condition.id, condition.options
                                .map(candidate => candidate.value)
                                .filter(value => value === option.value ? (e.target as HTMLInputElement).checked : chosen.includes(value))),
                        }),
                        m('span', option.label),
                    ])),
                ]);
            }),
            m('.db-v2-segment-actions', [
                m('button.btn.btn-sm.btn-flat-secondary', {type: 'button', onclick: () => this.close(true)}, labels.cancel ?? 'cancel'),
                m('button.btn.btn-sm.btn-primary', {
                    type: 'button',
                    onclick: () => {
                        const selection = appliedSelection(segment, draft);
                        this.close(true);
                        if (selection) {
                            attrs.onapply(selection);
                        }
                    },
                }, labels.apply ?? 'apply'),
            ]),
        ]);
    }
}
